import CodeBlock from "../components/CodeBlock";
import FAQItems from "../components/FAQItems";

const TEXT = "#EDEDED";
const TEXT_DIM = "#a0a0a0";
const TEAL = "#4FCDC4";
const CARD_BORDER = "rgba(255,255,255,0.04)";

function H2({ id, children }) {
  return (
    <h2
      id={id}
      style={{
        fontFamily: "var(--display)",
        fontWeight: 700,
        fontSize: "clamp(22px, 3vw, 28px)",
        color: TEXT,
        margin: "48px 0 16px",
        letterSpacing: "-0.01em",
        lineHeight: 1.2,
      }}
    >
      {children}
    </h2>
  );
}

function H3({ children }) {
  return (
    <h3
      style={{
        fontFamily: "var(--display)",
        fontWeight: 600,
        fontSize: 19,
        color: TEXT,
        margin: "28px 0 12px",
        lineHeight: 1.3,
      }}
    >
      {children}
    </h3>
  );
}

function P({ children }) {
  return (
    <p style={{ fontSize: 16, lineHeight: 1.75, color: TEXT_DIM, margin: "0 0 18px" }}>
      {children}
    </p>
  );
}

function Li({ children }) {
  return (
    <li style={{ fontSize: 16, lineHeight: 1.7, color: TEXT_DIM, marginBottom: 10 }}>
      {children}
    </li>
  );
}

function Strong({ children }) {
  return <strong style={{ color: TEXT, fontWeight: 600 }}>{children}</strong>;
}

function A({ href, children }) {
  return (
    <a
      href={href}
      style={{ color: TEAL, textDecoration: "none", borderBottom: `1px solid ${TEAL}44` }}
      {...(href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    >
      {children}
    </a>
  );
}

function Code({ children }) {
  return (
    <code
      style={{
        background: "rgba(255,255,255,0.06)",
        padding: "2px 6px",
        borderRadius: 4,
        fontFamily: "var(--mono)",
        fontSize: 13,
        color: TEXT,
      }}
    >
      {children}
    </code>
  );
}

function PullQuote({ children }) {
  return (
    <blockquote
      style={{
        margin: "32px 0",
        padding: "20px 24px",
        borderLeft: `3px solid ${TEAL}`,
        background: "rgba(79,205,196,0.05)",
        borderRadius: "0 12px 12px 0",
        fontFamily: "var(--display)",
        fontSize: 19,
        lineHeight: 1.5,
        color: TEXT,
        fontWeight: 500,
      }}
    >
      {children}
    </blockquote>
  );
}

function Callout({ kind = "info", title, children }) {
  const palette =
    kind === "warn"
      ? { bg: "rgba(239,68,68,0.08)", border: "rgba(239,68,68,0.25)", titleColor: "#f87171" }
      : { bg: "rgba(167,139,250,0.08)", border: "rgba(167,139,250,0.25)", titleColor: "#a78bfa" };
  return (
    <div
      style={{
        margin: "24px 0",
        padding: "18px 22px",
        borderRadius: 12,
        background: palette.bg,
        border: `1px solid ${palette.border}`,
      }}
    >
      <div
        style={{
          fontFamily: "var(--display)",
          fontWeight: 600,
          fontSize: 15,
          color: palette.titleColor,
          marginBottom: 8,
        }}
      >
        {title}
      </div>
      <div style={{ fontSize: 15, lineHeight: 1.7, color: TEXT_DIM }}>{children}</div>
    </div>
  );
}

function InlineCTA({ children, href, label }) {
  return (
    <div
      style={{
        margin: "36px 0",
        padding: "24px 28px",
        borderRadius: 14,
        background: "rgba(79,205,196,0.06)",
        border: "1px solid rgba(79,205,196,0.2)",
      }}
    >
      <div style={{ fontSize: 15, lineHeight: 1.7, color: TEXT_DIM, marginBottom: 14 }}>{children}</div>
      <a
        href={href}
        style={{
          display: "inline-block",
          borderRadius: 10,
          padding: "12px 28px",
          fontSize: 15,
          fontWeight: 600,
          minHeight: 44,
          background: TEAL,
          color: "#000",
          textDecoration: "none",
        }}
      >
        {label}
      </a>
    </div>
  );
}

function ScenarioTable() {
  const rows = [
    ["Paid click, canonical UTMs", "chatgpt / cpc", "Paid Other", "Paid Search requires the source to be on Google's search-sites list; chatgpt is not on it. One custom channel group rule gives it its own channel."],
    ["Paid click, no UTMs", "chatgpt.com / ai-assistant, or (direct)", "AI Assistants, or Direct", "Indistinguishable from organic. This is the case tagging exists to prevent; OpenAI's oppref click ID is on the URL but GA4 does not read it."],
    ["Organic citation click", "chatgpt.com / ai-assistant", "AI Assistants", "ChatGPT auto-appends utm_source=chatgpt.com; GA4 rewrites the medium to ai-assistant and the campaign to (ai-assistant) when the referrer matches its AI-assistant list."],
    ["Organic click before mid-May 2026", "chatgpt.com / referral", "Referral", "The AI Assistants channel is forward-only. Historical sessions keep their original classification."],
    ["AI crawler or agent visit", "not recorded", "none", "GPTBot and agent traffic do not execute JavaScript, so the GA4 web stream never sees them. Server-side collection is the only path."],
  ];
  return (
    <div style={{ background: "#0f0f11", borderRadius: 12, border: `1px solid ${CARD_BORDER}`, padding: 24, margin: "16px 0 32px", overflow: "hidden" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 4, fontFamily: "var(--body)" }}>What ChatGPT traffic actually shows as in GA4</div>
      <div style={{ fontSize: 10, color: TEXT_DIM, marginBottom: 16, fontFamily: "var(--body)" }}>Verified against Google&#39;s published channel definitions and source-category list, August 12, 2026. Session source / medium shown as source / medium.</div>
      <div className="blog-scroll-wrap" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", margin: "0 -4px" }}>
        <table style={{ width: "100%", minWidth: 680, borderCollapse: "collapse", fontFamily: "var(--body)", fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.15)" }}>
              <th style={{ textAlign: "left", padding: "12px 14px", color: TEAL, fontWeight: 700, fontSize: 12, letterSpacing: "0.04em", textTransform: "uppercase", fontFamily: "var(--mono)" }}>Scenario</th>
              <th style={{ textAlign: "left", padding: "12px 14px", color: TEXT, fontWeight: 700, fontSize: 12, letterSpacing: "0.04em", textTransform: "uppercase", fontFamily: "var(--mono)" }}>Source / medium</th>
              <th style={{ textAlign: "left", padding: "12px 14px", color: TEXT, fontWeight: 700, fontSize: 12, letterSpacing: "0.04em", textTransform: "uppercase", fontFamily: "var(--mono)" }}>Default channel</th>
              <th style={{ textAlign: "left", padding: "12px 14px", color: TEAL, fontWeight: 700, fontSize: 12, letterSpacing: "0.04em", textTransform: "uppercase", fontFamily: "var(--mono)" }}>Why</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([sc, sm, ch, why], i) => (
              <tr key={sc} style={{ borderBottom: i === rows.length - 1 ? "none" : "1px solid rgba(255,255,255,0.06)" }}>
                <td style={{ padding: "13px 14px", color: TEXT, fontWeight: 600, verticalAlign: "top" }}>{sc}</td>
                <td style={{ padding: "13px 14px", color: TEXT_DIM, fontFamily: "var(--mono)", fontSize: 13, verticalAlign: "top" }}>{sm}</td>
                <td style={{ padding: "13px 14px", color: TEXT, verticalAlign: "top" }}>{ch}</td>
                <td style={{ padding: "13px 14px", color: TEXT_DIM, verticalAlign: "top" }}>{why}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── FAQs ──
const faqs = [
  {
    q: "What does ChatGPT Ads traffic show up as in GA4?",
    a: "It depends entirely on your tagging. A paid click tagged with the canonical utm_source=chatgpt and utm_medium=cpc reports as source chatgpt, medium cpc, default channel Paid Other. An untagged paid click arrives looking like organic ChatGPT traffic: if the referrer survives, GA4 rewrites the medium to ai-assistant and it lands in the AI Assistants channel; if the referrer is stripped, it lands in Direct. Organic ChatGPT citation clicks carry the auto-appended utm_source=chatgpt.com and land in AI Assistants. There is no scenario where GA4 identifies a paid ChatGPT click on its own.",
  },
  {
    q: "Does GA4 classify ChatGPT Ads as Paid Search?",
    a: "No. GA4&#39;s Paid Search channel requires two conditions: a paid medium (cpc qualifies) AND a source on Google&#39;s published search-sites list. That list contains no AI assistant domains (we verified all 820 rows of Google&#39;s source-category list in August 2026: no chatgpt, no chatgpt.com, no perplexity, gemini, copilot, or claude). So utm_source=chatgpt with utm_medium=cpc lands in Paid Other. It is still cleanly separated as paid traffic, and a one-rule custom channel group keyed on session source matching chatgpt gives ChatGPT Ads its own named channel in every default report.",
  },
  {
    q: "What is the AI Assistants channel in GA4?",
    a: "A default channel Google rolled out from mid-May 2026 for traffic arriving from AI assistants such as ChatGPT, Gemini, Copilot, Grok, and DeepSeek. Per Google&#39;s channel definitions, a session lands there when the medium exactly matches ai-assistant, and GA4 sets that medium itself (with campaign set to (ai-assistant)) when the referrer matches its list of AI assistants. The classification is forward-only: sessions from before the rollout keep their original Referral or Direct classification, which is why year-over-year AI-traffic comparisons in GA4 break at that boundary.",
  },
  {
    q: "Do ChatGPT ad clicks end up in the AI Assistants channel?",
    a: "Tagged clicks should not: an explicit utm_medium=cpc places the session in paid channels. Untagged ad clicks can, because without your UTMs a paid click is indistinguishable from an organic one, and GA4&#39;s rewrite fires on the referrer. One honest caveat: Google documents when it sets the ai-assistant medium, but does not document whether that rewrite can override manually set UTM parameters, so the deterministic strategy is to tag every paid destination URL and monitor the split rather than assume.",
  },
  {
    q: "Does OpenAI send ChatGPT Ads conversions into GA4?",
    a: "No. There is no OpenAI-to-GA4 integration anywhere in OpenAI&#39;s ads documentation (OpenAI does document a beta connector in the other direction, which reads GA4 data into Ads Manager). The Measurement Pixel and Conversions API report conversions into OpenAI&#39;s Ads Manager, which is a separate loop from GA4&#39;s event stream. To see ChatGPT Ads conversions in GA4 you mark your own key events and attribute them through your UTM tagging, exactly as you would for any other paid channel.",
  },
  {
    q: "How do I get ChatGPT Ads broken out as its own channel in GA4?",
    a: "Build a custom channel group with one rule: session source matches chatgpt (or exactly chatgpt if you want to exclude the organic chatgpt.com source), condition it on a paid medium if you tag consistently, and name the channel ChatGPT Ads. Custom channel groups apply to standard Acquisition reports and Explorations. Without it, tagged ChatGPT Ads traffic pools in Paid Other alongside every other paid platform whose source is not on Google&#39;s search list.",
  },
  {
    q: "Can GA4 see AI crawlers and agents visiting my site?",
    a: "No. GPTBot, ClaudeBot, PerplexityBot, and OAI-SearchBot do not execute JavaScript, so the GA4 web stream never records them (Vercel measured zero JavaScript executions across 500 million GPTBot fetches). Agent visits behave similarly. Seeing that traffic requires server-side collection: a server-side GTM setup can classify AI crawler, agent, and referral traffic into GA4 events, which is what our free open-source AI Crawler Analytics template does.",
  },
];

function Content() {
  return (
    <article>
      {/* ── Featured-snippet box ── */}
      <div
        style={{
          margin: "0 0 32px",
          padding: "20px 24px",
          borderRadius: 12,
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.07)",
        }}
      >
        <P>
          <Strong>
            To track ChatGPT Ads in GA4: tag every ad destination URL with utm_source=chatgpt and utm_medium=cpc, build a one-rule custom channel group named ChatGPT Ads, mark your own conversion events, and know what the defaults do without you. Tagged paid clicks land in Paid Other (not Paid Search: chatgpt is not on Google&#39;s search-sites list). Organic ChatGPT clicks land in the AI Assistants channel GA4 rolled out from mid-May 2026. Untagged paid clicks blend into one of those two, unrecoverably. OpenAI reports its own conversions in Ads Manager and sends nothing to GA4.
          </Strong>
        </P>
        <ul style={{ paddingLeft: 20, margin: "0 0 0" }}>
          <Li><Strong>Tag:</Strong> <Code>utm_source=chatgpt&amp;utm_medium=cpc&amp;utm_campaign=&#123;campaign-slug&#125;&amp;utm_content=&#123;ad-variant&#125;</Code></Li>
          <Li><Strong>Default channel for tagged paid:</Strong> Paid Other, by Google&#39;s two-condition Paid Search rule</Li>
          <Li><Strong>Organic side:</Strong> auto-tagged <Code>utm_source=chatgpt.com</Code>, rewritten to medium <Code>ai-assistant</Code>, channel AI Assistants</Li>
          <Li><Strong>Fix the pooling:</Strong> one custom channel group rule: session source matches <Code>chatgpt</Code></Li>
          <Li><Strong>Conversions:</Strong> OpenAI&#39;s pixel reports to Ads Manager only; GA4 needs its own key events</Li>
          <Li><Strong>Crawlers and agents:</Strong> invisible to web GA4; server-side collection only</Li>
        </ul>
      </div>

      {/* ── Intro ── */}
      <P>
        Ask five guides what ChatGPT Ads traffic looks like in Google Analytics 4 and you will get five confident, mutually exclusive answers: it is Referral, it is Direct, it is Paid Search if you tag it, GA4 has a native integration, GA4 has nothing. We ran the top queries through the AI engines while researching this post and watched them cite sources that contradict each other on exactly those basics.
      </P>
      <P>
        The contradictions have a simple cause: <Strong>the ground truth changed, and most guides were right on the day they were written.</Strong> Google shipped a dedicated AI Assistants channel in mid-May 2026 that rewrote where organic AI traffic lands, forward-only, so pre-May guides describe a world that no longer exists and post-May guides describe one that did not exist before. This post is the date-scoped version: every claim below is checked against Google&#39;s current published channel definitions and source-category list, OpenAI&#39;s current ads documentation, and our own shipped measurement tooling, with verification dates stated.
      </P>

      <PullQuote>
        Every popular answer was right once. GA4 changed underneath them.
      </PullQuote>

      {/* ── The table ── */}
      <H2 id="what-ga4-actually-shows">What ChatGPT traffic actually shows as in GA4</H2>
      <P>
        Five scenarios cover everything. Two of them are healthy, one is recoverable, and two are structural limits you design around.
      </P>
      <ScenarioTable />
      <P>
        The row that costs real money is the second one. An untagged paid click is not misclassified, it is <Strong>unclassifiable</Strong>: nothing distinguishes it from an organic visit once it leaves ChatGPT, because OpenAI&#39;s <Code>oppref</Code> click identifier rides the URL for OpenAI&#39;s own attribution and GA4 does not read it. Whatever you do with the rest of this guide, tag every paid destination URL first.
      </P>

      {/* ── Canonical setup ── */}
      <H2 id="the-canonical-setup">The canonical tagging setup</H2>
      <P>
        The convention we use and recommend, unchanged since May 2026: <Code>utm_source=chatgpt</Code>, <Code>utm_medium=cpc</Code>, plus <Code>utm_campaign</Code>, <Code>utm_content</Code>, and optionally <Code>utm_term</Code> for the context-hint cluster. The full field-by-field reasoning lives in our <A href="/blog/how-to-add-utm-codes-to-chatgpt-ads">UTM guide for ChatGPT Ads</A>; the short version is that <Code>cpc</Code> keeps the traffic in GA4&#39;s paid buckets and matches the convention your Google Ads and Bing Ads tagging already uses.
      </P>
      <CodeBlock lang="Tagged destination URL" syntax="text">{`https://yoursite.com/landing?utm_source=chatgpt&utm_medium=cpc&utm_campaign=q3-launch&utm_content=headline-a&utm_term=cluster-1`}</CodeBlock>
      <P>
        One correction to the claim you will find in most guides, including, until this post shipped, ours: <Strong>this does not land in Paid Search.</Strong> Google&#39;s Paid Search channel requires the medium to match its paid regex <Strong>and</Strong> the source to be on its published search-sites list. We downloaded that list on August 12, 2026 and checked all 820 rows: it contains no AI assistant domains at all. So <Code>chatgpt</Code> + <Code>cpc</Code> lands in <Strong>Paid Other</Strong>, which is still a cleanly paid bucket, just a shared one.
      </P>
      <H3>Give it its own channel in one rule</H3>
      <P>
        In Admin, under Data display, choose Channel groups, create a custom channel group, and add a channel named <Strong>ChatGPT Ads</Strong> with the rule: session source exactly matches <Code>chatgpt</Code>. Reorder it above Paid Other so it wins. If you also want the organic side named, add a second channel keyed on session source exactly matches <Code>chatgpt.com</Code>. Custom channel groups apply in standard reports and Explorations, so this is the one setup step that makes every later report readable.
      </P>

      {/* ── AI Assistants channel ── */}
      <H2 id="the-ai-assistants-channel">The AI Assistants channel, and what Google does not document</H2>
      <P>
        From mid-May 2026, GA4 ships a default channel for AI-assistant traffic. Google&#39;s channel definitions describe it as traffic from sources like ChatGPT, Gemini, DeepSeek, Copilot, and Grok, and define it mechanically: the session lands there when the medium exactly matches <Code>ai-assistant</Code>, and GA4 itself sets that medium, with campaign <Code>(ai-assistant)</Code>, when the referrer matches its list of AI assistants. Organic ChatGPT citation clicks, which arrive carrying the auto-appended <Code>utm_source=chatgpt.com</Code>, land here.
      </P>
      <P>
        Two things Google does not publish, stated plainly because they matter operationally. First, the AI-assistant referrer list itself is unpublished, unlike the search-sites list, so you cannot audit which assistants are recognized. Second, the definition does not say whether the automatic rewrite can override manually set UTM parameters. We have found no documented answer either way, so this guide does not claim one. The deterministic posture: tag every paid URL, watch the paid channel and AI Assistants channel for leakage in either direction, and treat any tagged session that lands in AI Assistants as a signal to re-check, not as a mystery.
      </P>
      <Callout kind="warn" title="The channel is forward-only">
        Sessions from before the rollout keep their old classification, mostly Referral. Any year-over-year comparison of AI traffic in GA4 breaks at the mid-May 2026 boundary, and any guide that says organic ChatGPT traffic shows as Referral is describing the era before it.
      </Callout>

      {/* ── Conversions ── */}
      <H2 id="conversions-two-loops">Conversions: OpenAI&#39;s loop and GA4&#39;s loop never touch</H2>
      <P>
        OpenAI&#39;s <A href="/blog/how-to-install-chatgpt-ads-pixel-in-google-tag-manager">Measurement Pixel</A> and Conversions API report conversions into OpenAI&#39;s Ads Manager: that is where campaign CPA, oCPC optimization, and OpenAI&#39;s own attribution live. Nothing in OpenAI&#39;s ads documentation sends any of it to GA4. The one native GA4 link OpenAI documents runs the other way: a beta connector, available through your account manager, that lets OpenAI read your GA4 data to support optimization and compare attribution (October 2026). GA4 sees a ChatGPT Ads conversion only the way it sees any conversion: your site fires an event, you mark it as a key event, and attribution flows from the session&#39;s UTMs.
      </P>
      <P>
        Run both loops. Ads Manager conversions feed OpenAI&#39;s bidding; GA4 key events put ChatGPT Ads in the same reporting frame as every other channel, which is the comparison your budget decisions actually use. The stitching between the browser pixel and server-side events, the <Code>event_id</Code> deduplication key and the rest of the identifier plumbing, is its own topic: our <A href="/blog/chatgpt-ads-conversion-tracking#four-identifiers">four-identifier map</A> covers it, including the exact error the popular community server tag throws when the dedup key is missing.
      </P>

      {/* ── Server-side ── */}
      <H2 id="what-web-ga4-cannot-see">What web GA4 cannot see, and the server-side fix</H2>
      <P>
        Two classes of ChatGPT-related traffic never appear in a web GA4 property at all. AI crawlers (GPTBot, OAI-SearchBot, and peers) do not execute JavaScript, so no web tag ever fires for them. And an untagged paid click&#39;s <Code>oppref</Code> parameter, OpenAI&#39;s own click identifier, is invisible to GA4&#39;s channel logic even though it is sitting on the URL.
      </P>
      <P>
        Both are addressable server-side. Our free, open-source <A href="https://github.com/AI-Advisors/ai-crawler-analytics">AI Crawler Analytics template</A> for server-side GTM classifies AI crawler, agent, and referral traffic into dedicated GA4 events, and as of its v1.1 release it reads <Code>oppref</Code> directly: a paid ChatGPT click with a stripped referrer and no UTMs, the exact click web GA4 cannot classify, arrives as an <Code>ai_referral_visit</Code> event with <Code>ai_medium=paid</Code>. It requires a GTM server container, which is a real prerequisite and not a footnote; if you run web-only GTM, the tagging discipline above is the part of this guide that applies to you.
      </P>

      {/* ── Reports ── */}
      <H2 id="reports-that-answer-questions">Three reports that answer the actual questions</H2>
      <H3>1. Is the channel driving traffic? (Acquisition, 2 minutes)</H3>
      <P>
        With the custom channel group applied, Reports, Acquisition, Traffic acquisition, switch the primary channel dimension to your custom group. ChatGPT Ads sits next to Paid Search and AI Assistants as a first-class row.
      </P>
      <H3>2. Which campaigns and creatives convert? (Exploration, 10 minutes)</H3>
      <P>
        Build a free-form Exploration with session source exactly <Code>chatgpt</Code> as a filter, dimensions session campaign and session manual ad content (your <Code>utm_campaign</Code> and <Code>utm_content</Code>), metrics sessions, key events, and session key event rate. That is the campaign-and-variant table Ads Manager cannot give you, because it reports OpenAI&#39;s conversions, not your GA4 key events.
      </P>
      <H3>3. Paid vs organic ChatGPT, one chart</H3>
      <P>
        Same Exploration pattern, filter session source matching regex <Code>^chatgpt(\.com)?$</Code>, breakdown by session default channel group (or your custom group). The paid line and the AI Assistants line on one time series is the chart that answers whether ads are buying you reach you were not already earning, which is the question that decides the budget.
      </P>

      <InlineCTA href="/platform/ai-analytics" label="See the AI Analytics platform">
        AI-Advisors tracks organic AI citations, AI referral traffic, and ChatGPT Ads performance in one time series, next to the Search Console data you already trust. The GA4 discipline above is the foundation; the platform is the layer that reads it for you.
      </InlineCTA>

      {/* ── FAQPage JSON-LD ── */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.q.replace(/&#39;/g, "'"),
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.a.replace(/&#39;/g, "'"),
              },
            })),
          }),
        }}
      />

      {/* ── FAQ section ── */}
      <H2 id="faq">Frequently Asked Questions</H2>
      <FAQItems faqs={faqs} />

      {/* ── Related reading ── */}
      <H2 id="related">Related Reading</H2>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><A href="/blog/how-to-add-utm-codes-to-chatgpt-ads">How to Add UTM Codes to ChatGPT Ads</A></Li>
        <Li><A href="/blog/chatgpt-ads-conversion-tracking">ChatGPT Ads Conversion Tracking Is Real: What OpenAI&#39;s April 30 Privacy Policy Update Tells You About the Roadmap</A></Li>
        <Li><A href="/blog/chatgpt-ads-automatic-advanced-matching">ChatGPT Ads Automatic Advanced Matching Turns On August 17</A></Li>
        <Li><A href="/blog/how-to-report-on-chatgpt-ad-campaigns">How to Report on ChatGPT Ad Campaigns: A 2026 Measurement Stack</A></Li>
        <Li><A href="/blog/how-to-install-chatgpt-ads-pixel-in-google-tag-manager">How to install the ChatGPT Ads Measurement Pixel via Google Tag Manager</A></Li>
      </ul>
    </article>
  );
}

const post = {
  slug: "how-to-track-chatgpt-ads-in-ga4",
  title: "How to Track ChatGPT Ads in GA4 (What Actually Shows Up, Verified)",
  seoTitle: "How to Track ChatGPT Ads in GA4: What Actually Shows Up",
  excerpt:
    "Guides contradict each other about ChatGPT Ads in GA4 because GA4 changed underneath them. The date-scoped answer, verified against Google's published channel definitions and source-category list: tagged paid clicks land in Paid Other (not Paid Search), organic lands in the new AI Assistants channel, untagged paid is unrecoverable, and OpenAI sends nothing to GA4. Plus the one-rule custom channel group and the three reports that matter.",
  metaDescription:
    "What ChatGPT Ads traffic really shows as in GA4, verified against Google's channel definitions: Paid Other, the AI Assistants channel, and the setup that works.",
  date: "2026-08-12",
  lastUpdated: "2026-10-13",
  readTime: "11 min read",
  category: "AI Ads",
  tags: ["ChatGPT Ads", "GA4", "Google Analytics 4", "AI Assistants channel", "UTM Tracking", "Attribution", "Paid Other"],
  Content,
};

export default post;
